import { type JSX, useMemo, Children } from 'react';
import { createPortal } from 'react-dom';

import * as styles from './UserActions.sty.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  DataTableCellContextProvider,
  DataTableColumnContextProvider,
  DataTableHeaderContextProvider,
  useDataTable,
} from '../../contexts/DataTableContext.js';
import type { Cell, Column, Header } from '../../hooks/useTable/types.js';

/**
 * @internal
 * Floating menu for right click events.
 */
export function CellActionFloatingMenu(): JSX.Element | undefined {
  const table = useDataTable();
  const { type, headerId, rowId, columnId, x, y } = table.getState()
    ?.floatingMenuInfo ?? {
    type: null,
    headerId: null,
    rowId: null,
    columnId: null,
  };

  const actions = useMemo(() => {
    if (type === 'headerAction' && columnId !== null) {
      return table.getUserActionsForColumn(columnId);
    }

    if (type === 'cellAction' && columnId !== null && rowId !== null) {
      return table.getUserActionsForCell(columnId, rowId);
    }

    return null;
  }, [rowId, columnId, type, table]);

  const menuOpen = type !== null && columnId !== null && actions !== null;

  // Find the right column and cell objects to provide
  const { header, column, cell } = useMemo<{
    header?: Header<unknown, unknown>;
    column?: Column<unknown, unknown>;
    cell?: Cell<unknown, unknown>;
  }>(() => {
    if (menuOpen === false) {
      return { column: undefined, cell: undefined };
    }

    const header = headerId ? table.getHeader(headerId) : undefined;
    const column = table.getColumn(columnId);

    let cell: Cell<unknown, unknown> | undefined = undefined;
    if (rowId) {
      const rowCells = table.getRow(rowId).getAllCells();
      const columnCell = rowCells.find((cell) => cell.column.id === columnId);

      cell = columnCell;
    }

    return { header, column, cell };
  }, [columnId, headerId, menuOpen, rowId, table]);

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (menuOpen === false) {
    return;
  }

  // We need to portal the entire floating menu to escape CSS transforms on ancestor elements.
  // This ensures transforms on parent containers don't affect the position of the trigger button.
  return createPortal(
    <Menu
      open={menuOpen}
      onOpenChange={(isOpen) => {
        if (isOpen === false) {
          table.setState((state) => {
            const newState = { ...state };
            newState.floatingMenuInfo = {
              type: null,
              headerId: null,
              rowId: null,
              columnId: null,
              x: 0,
              y: 0,
            };
            newState.openMenuRowId = null;
            return newState;
          });
        }
      }}
      modal={false}
    >
      <Menu.Trigger>
        <button
          disabled
          type="button"
          className={styles.floatingMenuButton}
          style={{
            top: `${y}px`,
            left: `${x}px`,
          }}
          data-trigger="context-action"
        />
      </Menu.Trigger>
      {menuOpen === true && (
        <DataTableHeaderContextProvider value={header}>
          <DataTableColumnContextProvider value={column}>
            <DataTableCellContextProvider value={cell}>
              <Menu.Content>
                {/*
                 * Run all actions through a React.Children map so they will receive
                 * an automatic key and do not violate react key map rules.
                 */}
                {Children.map(actions, (child) => child)}
              </Menu.Content>
            </DataTableCellContextProvider>
          </DataTableColumnContextProvider>
        </DataTableHeaderContextProvider>
      )}
    </Menu>,
    document.body,
  );
}
